import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import FloatingContact from '../../../components/feature/FloatingContact';

export default function DogalIsikMimarideMetkanAlgisi() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Doğal Işık ve Mimarlıkta Mekân Algısı | Levent Atalay';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Işık mimarlığın en temel malzemesidir. Doğal ışığın mekân algısını nasıl dönüştürdüğünü ve tasarımda nasıl kullanıldığını inceleyin.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <FloatingContact />
      
      {/* Hero Image */}
      <div className="relative w-full h-96 md:h-[500px] mt-20">
        <img
          src="https://public.readdy.ai/ai/img_res/c0fb7eefde6e74d9d85656506150dfd7.jpg"
          alt="Doğal Işık ve Mekân"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-6 py-16">
        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
          <span className="text-gray-900 font-medium">Mekân Tasarımı</span>
          <span>•</span>
          <span>12 Ocak 2025</span>
          <span>•</span>
          <span>10 dk okuma</span>
        </div>

        {/* Title */}
        <h1 className="font-serif text-4xl md:text-5xl text-gray-900 mb-8 leading-tight">
          Doğal Işık ve Mimarlıkta Mekân Algısı
        </h1>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 leading-relaxed mb-6">
            Louis Kahn'ın dediği gibi, "Işık, tüm varlıkların yaratıcısıdır." Mimarlıkta ışık, sadece görmeyi sağlayan bir araç değil, mekânı şekillendiren, atmosfer yaratan ve kullanıcı deneyimini belirleyen temel bir elemandır. Doğal ışık, mimari tasarımın en değerli ve en dinamik malzemesidir.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Işığın Mimari Değeri
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Doğal ışık, mekâna hayat verir. Günün farklı saatlerinde değişen ışık kalitesi, mekânın karakterini sürekli olarak dönüştürür. Sabahın yumuşak ışığı, öğlenin keskin gölgeleri, akşamın sıcak tonları; her biri mekâna farklı bir ruh katar.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Mimari tasarımda ışık, form kadar önemlidir. Bir mekânın nasıl algılandığı, büyük ölçüde ışığın o mekâna nasıl girdiğine bağlıdır. Işık, mekânın boyutlarını, dokularını ve renklerini ortaya çıkarır. Aynı mekân, farklı ışık koşullarında tamamen farklı deneyimler sunar.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Işık ve Mekân İlişkisi
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Doğal ışığın mekâna girişi, mimari tasarımın en kritik kararlarından biridir. Pencerelerin boyutu, konumu ve yönelimi, mekânın ışık kalitesini belirler. Güney cepheden gelen bol ışık, kuzey cepheden gelen yumuşak ve dengeli ışık, doğu ve batı cephelerden gelen dinamik ışık; her biri farklı mekânsal deneyimler yaratır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Işığın mekâna giriş şekli de önemlidir. Üstten gelen ışık (zenithal ışık), mekâna dramatik bir etki kazandırır. Yandan gelen ışık, derinlik ve perspektif yaratır. Yansıyan ışık, yumuşak ve homojen bir aydınlatma sağlar. Her ışık türü, mekânda farklı bir atmosfer oluşturur.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Gölge ve Kontrast
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Işık kadar gölge de mimarlıkta önemlidir. Gölge, ışığın varlığını hissettiren, derinlik ve boyut kazandıran bir elemandır. Işık ve gölge arasındaki kontrast, mekânın dramatik etkisini artırır ve mimari formları vurgular.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Tadao Ando'nun çalışmalarında görüldüğü gibi, kontrollü gölgeler mekâna mistik bir atmosfer katabilir. Işığın ve gölgenin dengeli kullanımı, mekânda ritim ve hiyerarşi yaratır. Bu denge, kullanıcının mekânı nasıl deneyimlediğini doğrudan etkiler.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Işık ve Malzeme Etkileşimi
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Doğal ışık, malzemelerin gerçek karakterini ortaya çıkarır. Ahşabın sıcaklığı, betonun soğukluğu, taşın dokusu; hepsi ışıkla birlikte anlam kazanır. Işık, malzemelerin rengini, dokusunu ve yüzey kalitesini vurgular.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Farklı malzemeler, ışığa farklı tepkiler verir. Parlak yüzeyler ışığı yansıtır, mat yüzeyler emer. Şeffaf malzemeler ışığı geçirir, opak malzemeler engeller. Bu etkileşimler, mekânın görsel zenginliğini artırır ve dinamik bir atmosfer yaratır.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Mevsimsel Değişimler
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Doğal ışık, mevsimlerle birlikte değişir. Güneşin yörüngesindeki değişimler, ışığın mekâna giriş açısını ve yoğunluğunu etkiler. Yaz aylarında yüksek açıdan gelen ışık, kış aylarında alçak açıdan gelen ışık; mekânda farklı gölge desenleri ve atmosferler yaratır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            İyi bir mimari tasarım, bu mevsimsel değişimleri dikkate alır. Yaz güneşinden korunma, kış güneşinden yararlanma stratejileri, hem enerji verimliliği hem de kullanıcı konforu açısından önemlidir. Saçaklar, brise-soleil'ler ve diğer gölgeleme elemanları, bu dengeyi sağlamaya yardımcı olur.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Işık ve Psikoloji
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Doğal ışık, insan psikolojisi üzerinde derin etkilere sahiptir. Bol doğal ışık alan mekânlar, kullanıcıların ruh halini olumlu yönde etkiler, enerji seviyelerini artırır ve genel refahı iyileştirir. Işık, sirkadiyen ritmi düzenler ve uyku kalitesini etkiler.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Mekânda doğal ışığın varlığı, dışarıyla bağlantı hissi yaratır. Bu bağlantı, özellikle kentsel yaşamda önemlidir. Pencerelerden görünen gökyüzü, değişen hava koşulları ve günün farklı saatleri, kullanıcıya zaman ve mekân farkındalığı kazandırır.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Işık Kontrolü ve Esneklik
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Doğal ışığın kontrolü, kullanıcı konforunu artırır. Panjurlar, perdeler, jaluzi sistemleri ve akıllı camlar, ışık miktarının ve kalitesinin ayarlanmasını sağlar. Bu esneklik, mekânın farklı kullanım senaryolarına adapte olmasını mümkün kılar.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Işık kontrolü, sadece görsel konfor için değil, aynı zamanda termal konfor için de önemlidir. Aşırı güneş kazancı, mekânın aşırı ısınmasına neden olabilir. Etkili gölgeleme stratejileri, hem görsel hem de termal konforu optimize eder.
          </p>

          <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mt-12 mb-6">
            Sonuç
          </h2>

          <p className="text-gray-600 leading-relaxed mb-6">
            Doğal ışık, mimarlığın en temel ve en değerli malzemesidir. Mekânı şekillendirir, atmosfer yaratır ve kullanıcı deneyimini belirler. İyi bir mimari tasarım, ışığı sadece fonksiyonel bir ihtiyaç olarak değil, mekânın ruhunu oluşturan bir eleman olarak ele alır.
          </p>

          <p className="text-gray-600 leading-relaxed mb-6">
            Işık ve mekân arasındaki ilişkiyi anlamak, başarılı mimari tasarımın anahtarıdır. Her proje, kendi ışık stratejisini gerektirir. Konum, yönelim, iklim ve kullanım senaryoları, ışık tasarımını şekillendirir. Sonuçta, ışık sadece mekânı aydınlatmaz; onu yaşanabilir, anlamlı ve güzel kılar.
          </p>
        </div>

        {/* Back to Blog */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-900 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            <span>Tüm Yazılara Dön</span>
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}